"use client";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { ChevronDown, ChevronUp, ChevronLeft } from "lucide-react";
import { getCategories, getSubCategories } from "@/firebase/firestoreServices";
import type { Category, SubCategory } from "@/models";
import { motion, AnimatePresence } from "framer-motion";
import LazyImage from "@/components/LazyImage";

const CategoriesPage = () => {
    const router = useRouter();
    const [categories, setCategories] = useState<Category[]>([]);
    const [expandedCatId, setExpandedCatId] = useState<string | null>(null);
    const [subCatMap, setSubCatMap] = useState<Record<string, SubCategory[]>>({});
    const [loading, setLoading] = useState(true);
    const [loadingSubCats, setLoadingSubCats] = useState(false);

    useEffect(() => {
        const fetchCats = async () => {
            try {
                const data = await getCategories();
                // Sort by order if available
                const sorted = (data as Category[]).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                setCategories(sorted);
                if (sorted.length > 0) {
                    setExpandedCatId(sorted[0].id);
                }
            } catch (e) {
                console.error("Error fetching categories:", e);
            } finally {
                setLoading(false);
            }
        };
        fetchCats();
    }, []);

    useEffect(() => {
        if (!expandedCatId || subCatMap[expandedCatId]) return;

        const fetchSubs = async () => {
            setLoadingSubCats(true);
            try {
                const subs = await getSubCategories(expandedCatId);
                setSubCatMap(prev => ({ ...prev, [expandedCatId]: subs as SubCategory[] }));
            } catch (e) {
                console.error("Error fetching subcategories:", e);
            } finally {
                setLoadingSubCats(false);
            }
        };
        fetchSubs();
    }, [expandedCatId, subCatMap]);

    const toggleExpand = (id: string) => {
        setExpandedCatId(expandedCatId === id ? null : id);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto pb-20">
            {/* Header */}
            <div className="flex items-center gap-2 mb-6 sticky top-0 bg-background/80 backdrop-blur-sm py-3 z-10 px-2 lg:px-0">
                <button
                    onClick={() => router.back()}
                    className="p-2 hover:bg-muted rounded-full transition-colors"
                    aria-label="Go back"
                >
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <h1 className="text-2xl font-bold tracking-tight">All Categories</h1>
            </div>

            {/* Category List */}
            <div className="space-y-1 divide-y divide-border/40 px-2 lg:px-0">
                {categories.map((cat) => {
                    const isExpanded = expandedCatId === cat.id;
                    const subs = subCatMap[cat.id] || [];

                    return (
                        <div key={cat.id} className="py-2 first:pt-0 last:pb-0">
                            <button
                                onClick={() => toggleExpand(cat.id)}
                                className="w-full flex items-center justify-between py-3 text-left group transition-all"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="relative w-16 h-16 rounded-3xl overflow-hidden bg-muted flex-shrink-0 shadow-sm border border-border/5 group-hover:border-primary/20 transition-all">
                                        <LazyImage
                                            src={cat.image || ""}
                                            alt={cat.name}
                                            className="w-full h-full object-cover"
                                            wrapperClassName="w-full h-full"
                                        />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-[17px] text-foreground group-hover:text-primary transition-colors">{cat.name}</h3>
                                        {cat.description ? (
                                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{cat.description}</p>
                                        ) : (
                                            <p className="text-xs text-muted-foreground mt-0.5">Explore our {cat.name.toLowerCase()} collection</p>
                                        )}
                                    </div>
                                </div>
                                <div className={`p-1.5 rounded-full transition-all ${isExpanded ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}>
                                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                </div>
                            </button>

                            <AnimatePresence initial={false}>
                                {isExpanded && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                                        className="overflow-hidden"
                                    >
                                        <div className="pt-2 pb-6 grid grid-cols-4 gap-x-2 gap-y-6">
                                            {loadingSubCats && !subCatMap[cat.id] ? (
                                                Array.from({ length: 4 }).map((_, i) => (
                                                    <div key={i} className="flex flex-col items-center gap-2">
                                                        <div className="w-16 h-16 rounded-full bg-muted animate-pulse" />
                                                        <div className="w-12 h-2.5 bg-muted rounded animate-pulse" />
                                                    </div>
                                                ))
                                            ) : subs.length > 0 ? (
                                                subs.map((sub) => (
                                                    <button
                                                        key={sub.id}
                                                        onClick={() => router.push(`/category/${cat.id}?sub=${sub.id}`)}
                                                        className="flex flex-col items-center gap-2 group/sub active:scale-95 transition-transform"
                                                    >
                                                        <div className="w-16 h-16 p-0.5 rounded-full overflow-hidden bg-white border border-border/50 group-hover/sub:border-primary/40 shadow-sm transition-all">
                                                            <div className="w-full h-full rounded-full overflow-hidden">
                                                                <LazyImage
                                                                    src={sub.imageUrl || ""}
                                                                    alt={sub.name}
                                                                    className="w-full h-full object-cover group-hover/sub:scale-110 transition-transform duration-500"
                                                                    wrapperClassName="w-full h-full"
                                                                />
                                                            </div>
                                                        </div>
                                                        <span className="text-[11px] font-semibold text-center leading-tight line-clamp-2 px-0.5 text-foreground/90 group-hover/sub:text-primary transition-colors">
                                                            {sub.name}
                                                        </span>
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="col-span-full py-8 flex flex-col items-center justify-center text-muted-foreground bg-muted/20 rounded-2xl border border-dashed border-border/60">
                                                    <p className="text-xs font-medium">No subcategories found</p>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CategoriesPage;
